import { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail } from "lucide-react";

export default function UserFormDialog({ open, onOpenChange, user, onSuccess }) {
    const isEdit = !!user;
    const [form, setForm] = useState({ email: "", name: "", password: "", role: "member" });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setForm(
                user
                    ? { email: user.email, name: user.name, password: "", role: user.role || "member" }
                    : { email: "", name: "", password: "", role: "member" }
            );
        }
    }, [open, user]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.email || !form.name) {
            toast.error("Name and email are required");
            return;
        }
        if (isEdit && form.password && form.password.length < 6) {
            toast.error("New password must be at least 6 characters");
            return;
        }
        setSaving(true);
        try {
            if (isEdit) {
                const payload = { name: form.name, email: form.email, role: form.role };
                if (form.password) payload.password = form.password;
                await api.patch(`/users/${user.id}`, payload);
                toast.success("User updated");
            } else {
                await api.post("/users", { name: form.name, email: form.email, role: form.role });
                toast.success(`Invitation sent to ${form.email}`);
            }
            onSuccess?.();
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md" data-testid="user-form-dialog">
                <DialogHeader>
                    <DialogTitle className="text-lg font-semibold text-ph-text">
                        {isEdit ? "Edit user" : "Invite user"}
                    </DialogTitle>
                    <DialogDescription className="text-sm text-ph-muted">
                        {isEdit
                            ? "Update name, email, role, or reset the password."
                            : "We'll email an invitation link. The new user sets their own password to finish creating their account."}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={submit} className="space-y-4 pt-2" data-testid="user-form">
                    <div>
                        <label className="text-xs font-medium text-ph-text mb-1.5 block">Full name *</label>
                        <Input
                            required
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            data-testid="user-name-input"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-medium text-ph-text mb-1.5 block">Email *</label>
                        <Input
                            type="email"
                            required
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            data-testid="user-email-input"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-medium text-ph-text mb-1.5 block">Role *</label>
                        <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                            <SelectTrigger data-testid="user-role-trigger"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="member">Member</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                        </Select>
                        <p className="text-[11px] text-ph-mutedSoft mt-1">
                            Admins manage users and the full pipeline. Members use the pipeline only.
                        </p>
                    </div>
                    {isEdit && (
                        <div>
                            <label className="text-xs font-medium text-ph-text mb-1.5 block">Reset password (optional)</label>
                            <Input
                                type="password"
                                placeholder="Leave blank to keep current"
                                value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                                data-testid="user-password-input"
                            />
                        </div>
                    )}
                    {!isEdit && (
                        <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-ph-primarySoft border border-blue-100 text-xs text-ph-text">
                            <Mail className="w-3.5 h-3.5 mt-0.5 text-ph-primary shrink-0" />
                            <span>An invite link will be emailed. It expires in 7 days.</span>
                        </div>
                    )}

                    <div className="flex justify-end gap-3 pt-3 border-t border-ph-borderSoft">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="font-medium" data-testid="user-cancel-btn">
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={saving}
                            className="bg-ph-primary text-white font-medium hover:bg-ph-primaryHover"
                            data-testid="user-submit-btn"
                        >
                            {saving ? "Saving…" : isEdit ? "Save changes" : "Send invite"}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
